import { FC, useEffect, useState } from 'react';

import ModalDialog from '../../components/ModalDialog';
import SelectMenu from '../../components/SelectMenu';
import TextAreaGroup from '../../components/TextAreaGroup';
import { AnalyticsPage } from '../../../lib/analytics';
import { formatCurrency } from '../../lib/currency';
import {
  InvoiceOfflinePaymentMethod,
  ProgramInvoiceDataFragment,
} from '../../../generated/graphql';

const PAYMENT_METHOD_OPTIONS = [
  { label: 'Bank transfer', value: InvoiceOfflinePaymentMethod.BankTransfer },
  { label: 'Check', value: InvoiceOfflinePaymentMethod.Check },
  { label: 'Cash', value: InvoiceOfflinePaymentMethod.Cash },
  { label: 'Other', value: InvoiceOfflinePaymentMethod.Other },
];

const MAX_NOTE_LENGTH = 500;

type MarkInvoicePaidModalProps = {
  isOpen: boolean;
  invoice: ProgramInvoiceDataFragment | null;
  isSubmitting: boolean;
  onClose: () => void;
  onConfirm: (input: {
    paymentMethod: InvoiceOfflinePaymentMethod;
    note?: string;
  }) => void;
};

const MarkInvoicePaidModal: FC<MarkInvoicePaidModalProps> = ({
  isOpen,
  invoice,
  isSubmitting,
  onClose,
  onConfirm,
}) => {
  const [paymentMethod, setPaymentMethod] = useState(
    InvoiceOfflinePaymentMethod.BankTransfer,
  );
  const [note, setNote] = useState('');

  useEffect(() => {
    if (isOpen) {
      setPaymentMethod(InvoiceOfflinePaymentMethod.BankTransfer);
      setNote('');
    }
  }, [isOpen, invoice?.id]);

  const trimmedNote = note.trim();

  return (
    <ModalDialog
      title="Mark invoice as paid"
      originPage={AnalyticsPage.Payments}
      isOpen={isOpen}
      setClosed={onClose}
      width="small"
      fetching={isSubmitting}
      primaryActionTitle="Mark as paid"
      primaryActionDisabled={!paymentMethod || isSubmitting}
      primaryActionOnClick={() =>
        onConfirm({
          paymentMethod,
          note: trimmedNote || undefined,
        })
      }
    >
      <div className="flex flex-col gap-4">
        <p className="text-caption text-neutral-125">
          Record that {invoice?.patient?.name || 'this client'} paid invoice{' '}
          {invoice?.stripeInvoiceNumber} (
          {invoice ? formatCurrency(invoice.amount, invoice.currency) : ''})
          outside Stripe. This closes the Stripe invoice so they cannot also pay
          it online.
        </p>
        <SelectMenu
          label="Payment method"
          fieldValue={paymentMethod}
          fieldOptions={PAYMENT_METHOD_OPTIONS}
          onChange={(value) =>
            setPaymentMethod(value as InvoiceOfflinePaymentMethod)
          }
        />
        <TextAreaGroup
          label="Note (optional)"
          placeholder="Add a note for your records"
          rows={3}
          value={note}
          onChange={(event) => setNote(event.target.value)}
          maxLength={MAX_NOTE_LENGTH}
          characterCounter
          maxLengthValue={MAX_NOTE_LENGTH}
          currentLengthValue={note.length}
          inputSize="small"
        />
      </div>
    </ModalDialog>
  );
};

export default MarkInvoicePaidModal;
